import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  TableContainer,
  Spinner,
  Menu,
  MenuButton,
  MenuList,
  MenuItemOption,
  MenuOptionGroup,
  IconButton,
  Flex,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { useQuery } from 'react-query';
import { GetAllAlarms } from '../api/alarm';
import { format } from 'date-fns';
import { useAlarmDisplaySignalR } from '../hooks/useAlarmDisplaySignalR';
import { getApiErrorMessage } from '../utils/apiError';

const AlarmsForDisplay = () => {
  useAlarmDisplaySignalR(); // 📡 SignalR ile veriyi canlı dinle

  const {
    data: alarms,
    error: alarmError,
    isLoading: alarmLoading,
  } = useQuery('alarmsKey', GetAllAlarms, {
    refetchOnWindowFocus: false,
  });

  const [filters, setFilters] = useState({
    name: [],
    group: [],
    refetchOnWindowFocus: false,
  });

  const [filterSelections, setFilterSelections] = useState({
    name: [],
    group: [],
    refetchOnWindowFocus: false,
  });

  const [menuOpen, setMenuOpen] = useState({
    name: false,
    group: false,
    refetchOnWindowFocus: false,
  });

  const [tableHeight, setTableHeight] = useState(0);
  const tableRef = useRef(null);

  useEffect(() => {
    if (alarms) {
      setFilters({
        name: [...new Set(alarms.map(alarm => alarm.name))],
        group: [...new Set(alarms.map(alarm => alarm.group || 'No Group'))],
      });
    }
  }, [alarms]);

  useEffect(() => {
    if (tableRef.current) {
      setTableHeight(tableRef.current.clientHeight);
    }
  }, [tableRef]);

  const applyFilter = () => {
    return alarms.filter(alarm => {
      return (
        (!filterSelections.name.length ||
          filterSelections.name.includes(alarm.name)) &&
        (!filterSelections.group.length ||
          filterSelections.group.includes(alarm.group))
      );
    });
  };

  const handleFilterChange = (value, filterName) => {
    setFilterSelections(
      prev => ({
        ...prev,
        [filterName]: value,
      }),
      {
        refetchOnWindowFocus: false,
      }
    );
  };

  const handleMenuToggle = field => {
    setMenuOpen(prev => ({ ...prev, [field]: !prev[field] }));
  };

  if (alarmLoading) {
    return (
      <Box
        w="100%"
        h="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner />
      </Box>
    );
  }

  if (alarmError) {
    return (
      <Box w="100%" display="flex" justifyContent="center" p={5}>
        <Text>An error occurred: {getApiErrorMessage(alarmError)}</Text>
      </Box>
    );
  }

  const filteredAlarms = applyFilter();

  return (
    <Flex direction="column" h="100vh" overflow="hidden">
      <Flex justifyContent="space-between" alignItems="center" p={5}>
        <Heading size="md">Alarms</Heading>
      </Flex>
      <TableContainer flex="1" overflowY="auto" maxHeight="100%">
        <Table variant="simple" colorScheme="gray" size="sm">
          <Thead position="sticky" top={0} zIndex={1} bg="white">
            <Tr>
              <Th px={4} py={2}>
                Time
              </Th>
              <Th px={4} py={2}>
                Code
              </Th>
              <Th px={4} py={2} onClick={() => handleMenuToggle('group')}>
                Group
                <Menu
                  isOpen={menuOpen.group}
                  onClose={() => handleMenuToggle('group')}
                  style={{ maxHeight: tableHeight }}
                >
                  <MenuButton
                    as={IconButton}
                    icon={<ChevronDownIcon />}
                    variant="ghost"
                    aria-label="Filter Group"
                  />
                  <MenuList>
                    <MenuOptionGroup
                      type="checkbox"
                      value={filterSelections.group}
                      onChange={values => handleFilterChange(values, 'group')}
                    >
                      {filters.group.map(group => (
                        <MenuItemOption
                          key={group}
                          value={group}
                          style={{ width: '100%' }}
                        >
                          {group}
                        </MenuItemOption>
                      ))}
                    </MenuOptionGroup>
                  </MenuList>
                </Menu>
              </Th>
              <Th px={4} py={2} onClick={() => handleMenuToggle('name')}>
                Name
                <Menu
                  isOpen={menuOpen.name}
                  onClose={() => handleMenuToggle('name')}
                  style={{ maxHeight: tableHeight }}
                >
                  <MenuButton
                    as={IconButton}
                    icon={<ChevronDownIcon />}
                    variant="ghost"
                    aria-label="Filter Name"
                  />
                  <MenuList>
                    <MenuOptionGroup
                      type="checkbox"
                      value={filterSelections.name}
                      onChange={values => handleFilterChange(values, 'name')}
                    >
                      {filters.name.map(name => (
                        <MenuItemOption
                          key={name}
                          value={name}
                          style={{ width: '100%' }}
                        >
                          {name}
                        </MenuItemOption>
                      ))}
                    </MenuOptionGroup>
                  </MenuList>
                </Menu>
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredAlarms.map(alarm => (
              <Tr key={alarm.id}>
                <Td px={4} py={2}>
                  {alarm.time
                    ? format(new Date(alarm.time), 'yyyy-MM-dd HH:mm:ss')
                    : 'No Time Data'}
                </Td>
                <Td px={4} py={2}>
                  {alarm.code}
                </Td>
                <Td px={4} py={2}>
                  {alarm.group || 'No Group'}
                </Td>
                <Td px={4} py={2}>
                  {alarm.name}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Flex>
  );
};

export default AlarmsForDisplay;
